import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AsyncStatus,
  CashFlow,
  GroupedCashFlow,
  ActivityEntry,
  ActivityEntryByDate,
  ActivityError,
  Pagination,
  ActivityCashFlowError,
} from '@algoan/dashboard/model';
import { sortActivityEntryList } from './activity-utils';

export type ActivityEntryError = ActivityError | undefined;

export type CashFlowError = ActivityCashFlowError | undefined;

export interface ActivityReducerState {
  activityEntryList: ActivityEntry[];
  activityEntryListGroupedByDate: ActivityEntryByDate[];
  activityEntryError: ActivityEntryError;
  activityListFetchingStatus: AsyncStatus;

  activityCashFlow?: GroupedCashFlow | CashFlow;
  activityCashFlowCustomerId?: string;
  activityCashFlowAnalysisId?: string;
  activityCashFlowFetchingStatus: AsyncStatus;
  activityCashFlowError: CashFlowError;
  pagination: Pagination;
}

const initialPagination = { currentPage: 0 };

const getInitialState = (): ActivityReducerState => ({
  activityEntryList: [],
  activityEntryListGroupedByDate: [],
  activityEntryError: undefined,
  activityListFetchingStatus: AsyncStatus.IDLE,

  activityCashFlow: undefined,
  activityCashFlowCustomerId: undefined,
  activityCashFlowAnalysisId: undefined,
  activityCashFlowFetchingStatus: AsyncStatus.IDLE,
  activityCashFlowError: undefined,

  pagination: initialPagination,
});

const initialState = getInitialState();

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    fetchActivityEntryStart(state) {
      state.activityListFetchingStatus = AsyncStatus.PENDING;
      state.activityEntryError = undefined;
    },

    fetchActivityEntryFailure(
      state,
      action: PayloadAction<{
        error: ActivityEntryError;
      }>,
    ) {
      const { error } = action.payload;
      state.activityListFetchingStatus = AsyncStatus.REJECTED;
      state.activityEntryError = error;
    },

    fetchActivityEntrySuccess(
      state,
      action: PayloadAction<{
        activityEntryList: ActivityEntry[];
        paginationData: Pagination;
      }>,
    ) {
      const { activityEntryList, paginationData } = action.payload;
      state.activityEntryList = [...state.activityEntryList, ...activityEntryList];
      state.activityEntryListGroupedByDate = sortActivityEntryList(state.activityEntryList);
      state.activityListFetchingStatus = AsyncStatus.RESOLVED;
      state.activityEntryError = undefined;
      state.pagination = { ...state.pagination, ...paginationData };
    },

    resetActivityState() {
      return getInitialState();
    },

    fetchActivityCashFlowStart(state) {
      state.activityCashFlowFetchingStatus = AsyncStatus.PENDING;
      state.activityCashFlowError = undefined;
      state.activityCashFlow = undefined;
    },

    fetchActivityCashFlowFailure(
      state,
      action: PayloadAction<{
        error: ActivityCashFlowError;
      }>,
    ) {
      const { error } = action.payload;
      state.activityCashFlowFetchingStatus = AsyncStatus.REJECTED;
      state.activityCashFlowError = error;
    },

    fetchActivityCashFlowSuccess(
      state,
      action: PayloadAction<{
        customerId: string;
        analysisId: string;
        activityCashFlow: GroupedCashFlow | CashFlow;
      }>,
    ) {
      const { customerId, analysisId, activityCashFlow } = action.payload;
      state.activityCashFlowCustomerId = customerId;
      state.activityCashFlowAnalysisId = analysisId;
      state.activityCashFlow = activityCashFlow;
      state.activityCashFlowFetchingStatus = AsyncStatus.RESOLVED;
    },
  },
});

export const { reducer: activityReducer } = activitySlice;
export const {
  fetchActivityEntryStart,
  fetchActivityEntrySuccess,
  fetchActivityEntryFailure,
  resetActivityState,
  fetchActivityCashFlowStart,
  fetchActivityCashFlowFailure,
  fetchActivityCashFlowSuccess,
} = activitySlice.actions;
